import { useMutation, useQueryClient } from '@tanstack/react-query';
import { knowledgeQueryKeys } from '../../knowledge/knowledgeQueryKeys';
import { userService } from '../../../services/UserService';
import { tenantQueryKeys } from '../tenantQueryKeys';

function invalidateGroupAssignments(
  queryClient: ReturnType<typeof useQueryClient>,
  groupId?: string,
  userId?: string,
) {
  void queryClient.invalidateQueries({ queryKey: tenantQueryKeys.groups() });
  if (groupId) {
    void queryClient.invalidateQueries({
      queryKey: tenantQueryKeys.groupMembers(groupId),
    });
    void queryClient.invalidateQueries({
      queryKey: tenantQueryKeys.group(groupId),
    });
  }
  if (userId) {
    void queryClient.invalidateQueries({
      queryKey: tenantQueryKeys.userGroups(userId),
    });
  } else {
    void queryClient.invalidateQueries({
      queryKey: [...tenantQueryKeys.all, 'userGroups'],
    });
  }
  // Group membership changes can affect KDB access via group assignments.
  void queryClient.invalidateQueries({
    queryKey: [...knowledgeQueryKeys.all, 'members'],
  });
  void queryClient.invalidateQueries({
    queryKey: [...knowledgeQueryKeys.all, 'groupAssignments'],
  });
}

export function useCreateTenantGroupMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => userService.createGroup(name),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: tenantQueryKeys.groups(),
      });
    },
  });
}

export function useRenameTenantGroupMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, name }: { groupId: string; name: string }) =>
      userService.renameGroup(groupId, name),
    onSuccess: (_result, variables) => {
      invalidateGroupAssignments(queryClient, variables.groupId);
    },
  });
}

export function useDeleteTenantGroupMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId: string) => userService.deleteGroup(groupId),
    onSuccess: (_result, groupId) => {
      void queryClient.invalidateQueries({
        queryKey: tenantQueryKeys.groups(),
      });
      void queryClient.removeQueries({
        queryKey: tenantQueryKeys.groupMembers(groupId),
      });
      void queryClient.removeQueries({
        queryKey: tenantQueryKeys.group(groupId),
      });
      void queryClient.invalidateQueries({
        queryKey: [...tenantQueryKeys.all, 'userGroups'],
      });
      void queryClient.invalidateQueries({
        queryKey: [...knowledgeQueryKeys.all, 'groupAssignments'],
      });
      void queryClient.invalidateQueries({
        queryKey: [...knowledgeQueryKeys.all, 'members'],
      });
    },
  });
}

export function useAssignTenantUserGroupMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, groupId }: { userId: string; groupId: string }) =>
      userService.assignUserGroup(userId, groupId),
    onSuccess: (_result, variables) => {
      invalidateGroupAssignments(
        queryClient,
        variables.groupId,
        variables.userId,
      );
    },
  });
}

export function useUnassignTenantUserGroupMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, groupId }: { userId: string; groupId: string }) =>
      userService.unassignUserGroup(userId, groupId),
    onSuccess: (_result, variables) => {
      invalidateGroupAssignments(
        queryClient,
        variables.groupId,
        variables.userId,
      );
    },
  });
}
